/* ============================================================================
   DiveSitesPage — LOCKED VISUAL (AQUORIX Tropical Money, operator dashboard)
   ----------------------------------------------------------------------------
   Pixel-for-pixel, feature-for-feature port of the approved prototype
   (AQX_07-24-2026_Dive_Sites_Prototype_v3). This file and DiveSitesPage.css are
   the visual truth and must NOT change during wiring. All data and mutations
   arrive from useDiveSites() — wire the live system there, not here.

   Mounts inside the existing dashboard shell (which provides the sidebar and
   top bar). This component renders the page content only.
   ============================================================================ */
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDiveSites, DiveSite, MeetLocation, DiveCategory, NewCustomSite, NewMeetLocation } from "./useDiveSites";
import "./DiveSitesPage.css";

const SKILL_ORDER: Record<string, number> = { Beginner: 1, Intermediate: 2, Advanced: 3 };
type SortKey = "status" | "name" | "type" | "depth" | "skill";
type ModalKind = null | "edit" | "create" | "catalog" | "meetCreate" | "meetEdit" | "categoryEdit";

const dName = (s: DiveSite) => (s.isCustom ? s.name : s.customName || s.name);
const dDesc = (s: DiveSite) => s.customDescription || s.description;
// "Also known as" is always the canonical catalog name, auto-carried, only on renamed catalog sites.
const refLine = (s: DiveSite) => (s.isCustom ? "" : s.customName ? `also known as ${s.name}` : "");

const IconPlus = () => (<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>);
const IconSearch = () => (<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>);
const IconX = () => (<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>);
const IconCheck = () => (<svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 6" /></svg>);

export default function DiveSitesPage() {
  const api = useDiveSites();
  const [sectionView, setSectionView] = useState<"sites" | "meet" | "categories">("sites");
  const [view, setView] = useState<"list" | "cards">("list");
  const [search, setSearch] = useState("");
  const [meetSearch, setMeetSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [dir, setDir] = useState(1);
  const [modal, setModal] = useState<ModalKind>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [meetEditId, setMeetEditId] = useState<number | null>(null);

  const sites = api.sites;
  const meetLocations = api.meetLocations;
  const diveCategories = api.diveCategories;
  const editSite = editId != null ? sites.find(s => s.id === editId) || null : null;
  const editMeetLocation = meetEditId != null ? meetLocations.find(m => m.id === meetEditId) || null : null;

  const stats = useMemo(() => ({
    diving: sites.filter(s => s.inList && s.active).length,
    catalog: sites.filter(s => !s.isCustom).length,
    custom: sites.filter(s => s.isCustom).length,
    meet: meetLocations.filter(m => m.active).length,
  }), [sites, meetLocations]);

  const arrange = (rows: DiveSite[]) => {
    const q = search.trim().toLowerCase();
    const filtered = q ? rows.filter(s => dName(s).toLowerCase().includes(q) || s.name.toLowerCase().includes(q)) : rows;
    return [...filtered].sort((a, b) => {
      let av: number | string = 0, bv: number | string = 0;
      if (sort === "name") { av = dName(a).toLowerCase(); bv = dName(b).toLowerCase(); }
      else if (sort === "type") { av = a.type; bv = b.type; }
      else if (sort === "depth") { av = a.depthMax; bv = b.depthMax; }
      else if (sort === "skill") { av = SKILL_ORDER[a.skill]; bv = SKILL_ORDER[b.skill]; }
      else if (sort === "status") { av = a.active ? 1 : 0; bv = b.active ? 1 : 0; }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return dName(a).localeCompare(dName(b));
    });
  };

  const active = useMemo(() => arrange(sites.filter(s => s.inList && s.active)), [sites, search, sort, dir]);
  const archived = useMemo(() => arrange(sites.filter(s => s.inList && !s.active)), [sites, search, sort, dir]);
  const shownMeetLocations = useMemo(() => {
    const q = meetSearch.trim().toLowerCase();
    const rows = q
      ? meetLocations.filter(m => m.name.toLowerCase().includes(q))
      : meetLocations;
    return [...rows].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  }, [meetLocations, meetSearch]);

  const arrangedDiveCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    const rows = q
      ? diveCategories.filter(c => c.label.toLowerCase().includes(q))
      : diveCategories;
    return [...rows].sort((a, b) => Number(b.active) - Number(a.active) || a.label.localeCompare(b.label));
  }, [diveCategories, categorySearch]);

  const sortBy = (k: SortKey) => { if (sort === k) setDir(d => -d); else { setSort(k); setDir(1); } };
  const arw = (k: SortKey) => (sort === k ? <span className="ar">{dir > 0 ? "↑" : "↓"}</span> : null);

  const openEdit = (id: number) => { setEditId(id); setModal("edit"); };
  const openMeetEdit = (id: number) => { setMeetEditId(id); setModal("meetEdit"); };
  const close = () => { setModal(null); setEditId(null); setMeetEditId(null); };

  return (
    <div className="aqx-divesites">

      <div className="ds-content">
        <h1 className="ds-h1">Dive Sites &amp; Meet Locations</h1>
        <p className="ds-lede">Curate the places your center uses: the dive sites your team visits and the meet locations where customers gather before the day begins. These lists are what your crew picks from when they build the day.</p>

        <div className="ds-toolbar">
          <div className="ds-stats">
            <div className="ds-stat"><b>{stats.diving}</b><span>Sites you dive</span></div>
            <div className="ds-stat"><b>{stats.catalog}</b><span>{api.destinationName} catalog</span></div>
            <div className="ds-stat"><b>{stats.custom}</b><span>Custom sites</span></div>
            <div className="ds-stat"><b>{stats.meet}</b><span>Meet locations</span></div>
          </div>
        </div>

        <div className="ds-modes">
          <button className={sectionView === "sites" ? "on" : ""} onClick={() => setSectionView("sites")}>Dive Sites</button>
          <button className={sectionView === "meet" ? "on" : ""} onClick={() => setSectionView("meet")}>Meet Locations</button>
          <button className={sectionView === "categories" ? "on" : ""} onClick={() => setSectionView("categories")}>Dive Categories</button>
        </div>

        {sectionView === "sites" ? (
          <>
            <div className="ds-toolbar section-tools">
              <div className="ds-search"><IconSearch /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your sites" /></div>
              <div className="ds-seg">
                <button className={view === "list" ? "on" : ""} onClick={() => setView("list")}>List</button>
                <button className={view === "cards" ? "on" : ""} onClick={() => setView("cards")}>Cards</button>
              </div>
              <div className="ds-spacer" />
              <button className="ds-btn" onClick={() => setModal("create")}><IconPlus />Create custom site</button>
              <button className="ds-btn primary" onClick={() => setModal("catalog")}><IconPlus />Add from catalog</button>
            </div>

            <div className="ds-secrow"><span className="ds-seclabel">Sites you dive</span><span className="n">{active.length}</span></div>
            {active.length ? (view === "list" ? <SiteTable rows={active} {...{ sortBy, arw, openEdit, api }} /> : <SiteCards rows={active} {...{ openEdit, api }} />)
              : <div className="ds-empty">No sites yet. Use <b>Add from catalog</b> to pick the sites you dive.</div>}

            <div className="ds-secrow"><span className="ds-seclabel">Not currently diving</span><span className="n">{archived.length}</span></div>
            {archived.length ? (view === "list" ? <SiteTable rows={archived} {...{ sortBy, arw, openEdit, api }} /> : <SiteCards rows={archived} {...{ openEdit, api }} />)
              : <div className="ds-empty">Nothing inactive. Sites you stop diving can be reactivated or deleted.</div>}
          </>
        ) : sectionView === "meet" ? (
          <>
            <div className="ds-meet-head">
              <div>
                <span className="ds-seclabel">Meet locations</span>
                <h2>Where customers gather</h2>
                <p>Use meet locations for dive center check-in, docks, hotel pickup points, shore entries, and custom customer-facing arrival instructions.</p>
              </div>
              <div className="ds-meet-actions">
                <div className="ds-search"><IconSearch /><input value={meetSearch} onChange={e => setMeetSearch(e.target.value)} placeholder="Search meet locations" /></div>
                <button className="ds-btn primary" onClick={() => setModal("meetCreate")}><IconPlus />Create meet location</button>
              </div>
            </div>

            {shownMeetLocations.length
              ? <MeetLocationsTable rows={shownMeetLocations} openEdit={openMeetEdit} api={api} />
              : <div className="ds-empty">No meet locations yet. Create one place where customers gather before the day begins.</div>}
          </>
        ) : (
          <DiveCategoriesSection
            categories={arrangedDiveCategories}
            search={categorySearch}
            setSearch={setCategorySearch}
            onToggleActive={api.setDiveCategoryActive}
          />
        )}
      </div>

      {modal && createPortal(
        <div className="aqx-divesites">
          <div className="ds-scrim" onClick={close} />
          {modal === "edit" && editSite && <EditModal site={editSite} api={api} onClose={close} />}
          {modal === "create" && <CreateModal api={api} onClose={close} />}
          {modal === "catalog" && <CatalogModal sites={sites} api={api} onClose={close} />}
          {modal === "meetCreate" && <MeetLocationModal api={api} onClose={close} />}
          {modal === "meetEdit" && editMeetLocation && <MeetLocationModal meetLocation={editMeetLocation} api={api} onClose={close} />}
        </div>,
        document.body
      )}
    </div>
  );
}


/* ---------- meet locations ---------- */
function MeetLocationsTable({ rows, openEdit, api }: { rows: MeetLocation[]; openEdit: (id: number) => void; api: ReturnType<typeof useDiveSites>; }) {
  return (
    <div className="ds-tablewrap ds-meet-table">
      <table>
        <thead><tr>
          <th>Name</th>
          <th>Status</th>
          <th style={{ textAlign: "right" }}>Actions</th>
        </tr></thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.id}>
              <td>
                <button className="ds-sname" onClick={() => openEdit(m.id)}>{m.name}</button>
              </td>
              <td><span className={`ds-pill ${m.active ? "active" : "quiet"}`}><span className="d" />{m.active ? "Active" : "Inactive"}</span></td>
              <td className="act">
                <button className="ds-btn ghost" onClick={() => openEdit(m.id)}>Edit</button>
                <button className={m.active ? "ds-btn" : "ds-btn reef"} onClick={() => api.setMeetLocationActive(m.id, !m.active)}>{m.active ? "Deactivate" : "Reactivate"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- list ---------- */
function SiteTable({ rows, sortBy, arw, openEdit, api }: { rows: DiveSite[]; sortBy: (k: SortKey) => void; arw: (k: SortKey) => React.ReactNode; openEdit: (id: number) => void; api: ReturnType<typeof useDiveSites>; }) {
  const deleteSite = (site: DiveSite) => {
    if (!window.confirm(`Delete ${dName(site)} from your dive sites?`)) return;
    if (site.isCustom) api.deleteCustom(site.id);
    else api.removeFromList(site.id);
  };

  return (
    <div className="ds-tablewrap">
      <table>
        <thead><tr>
          <th className="nosort">#</th>
          <th onClick={() => sortBy("status")}>Status {arw("status")}</th>
          <th onClick={() => sortBy("name")}>Site name {arw("name")}</th>
          <th onClick={() => sortBy("type")}>Type {arw("type")}</th>
          <th onClick={() => sortBy("depth")}>Depth {arw("depth")}</th>
          <th onClick={() => sortBy("skill")}>Skill level {arw("skill")}</th>
          <th className="nosort" style={{ textAlign: "right" }}>Action</th>
        </tr></thead>
        <tbody>
          {rows.map((s, i) => {
            const ref = refLine(s);
            const desc = dDesc(s);
            return (
              <tr key={s.id}>
                <td className="num">{i + 1}</td>
                <td><span className={`ds-pill ${s.active ? "active" : "quiet"}`}><span className="d" />{s.active ? "Active" : "Inactive"}</span></td>
                <td>
                  <button className="ds-sname" onClick={() => openEdit(s.id)}>{dName(s)}</button>
                  {s.isCustom && <> <span className="ds-tag custom">Custom</span> <span className="ds-tag priv">Private</span></>}
                  <div className="ds-sdesc">{desc.slice(0, 110)}{desc.length > 110 ? "…" : ""}</div>
                  {ref && <div className="ds-sorig">{ref}</div>}
                </td>
                <td className="tcell">{s.type}</td>
                <td className="dcell">{s.depthMin}–{s.depthMax} m</td>
                <td className="scell">{s.skill}</td>
                <td className="act">
                  <button className="ds-btn ghost" onClick={() => openEdit(s.id)}>Edit</button>
                  <button className="ds-btn" onClick={() => api.setActive(s.id, !s.active)}>{s.active ? "Deactivate" : "Activate"}</button>
                  <button className="ds-btn danger" onClick={() => deleteSite(s)}>Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- cards ---------- */
function SiteCards({ rows, openEdit, api }: { rows: DiveSite[]; openEdit: (id: number) => void; api: ReturnType<typeof useDiveSites>; }) {
  const deleteSite = (site: DiveSite) => {
    if (!window.confirm(`Delete ${dName(site)} from your dive sites?`)) return;
    if (site.isCustom) api.deleteCustom(site.id);
    else api.removeFromList(site.id);
  };

  return (
    <div className="ds-cards">
      {rows.map(s => {
        const ref = refLine(s);
        const desc = dDesc(s);
        return (
          <div className={`ds-card ${s.active ? "" : "quiet"}`} key={s.id}>
            <div className="top">
              <span className={`ds-pill ${s.active ? "active" : "quiet"}`}><span className="d" />{s.active ? "Active" : "Inactive"}</span>
              {s.isCustom && <span className="ds-tag custom">Custom</span>}
            </div>
            <div>
              <button className="nm" onClick={() => openEdit(s.id)}>{dName(s)}</button>
              {ref && <div className="ds-sorig">{ref}</div>}
            </div>
            <div className="desc">{desc.slice(0, 120)}{desc.length > 120 ? "…" : ""}</div>
            <div className="chips">
              <span className="ds-chip water">{s.type}</span>
              <span className="ds-chip">{s.entry}</span>
              <span className="ds-chip">{s.skill}</span>
              <span className="ds-chip">{s.depthMin}–{s.depthMax}m</span>
            </div>
            <div className="foot">
              <button className="ds-btn" onClick={() => api.setActive(s.id, !s.active)}>{s.active ? "Deactivate" : "Activate"}</button>
              <div className="ds-spacer" />
              <button className="ds-btn ghost" onClick={() => openEdit(s.id)}>Edit →</button>
              <button className="ds-btn danger" onClick={() => deleteSite(s)}>Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- edit modal ---------- */
function EditModal({ site, api, onClose }: { site: DiveSite; api: ReturnType<typeof useDiveSites>; onClose: () => void; }) {
  const [name, setName] = useState(site.isCustom ? site.name : site.customName);
  const [desc, setDesc] = useState(site.customDescription || (site.isCustom ? site.description : ""));
  const [active, setActive] = useState(site.active);
  const [heroCaption, setHeroCaption] = useState(site.heroCaption || "");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  const orig = site.isCustom ? "Custom site · private to your center" : site.customName ? `Catalog site · also known as ${site.name}` : "Catalog site";
  const nameHint = site.isCustom ? "Your own site — the name is yours." : `The catalog calls it "${site.name}". Call it anything you like; the card always keeps "also known as ${site.name}".`;
  const preview = name.trim() || site.name;

  const save = () => {
    if (site.isCustom) api.saveOverlay(site.id, { name: name.trim() || site.name, description: desc.trim() || site.description, active });
    else api.saveOverlay(site.id, { customName: name.trim(), customDescription: desc.trim(), active });
    onClose();
  };

  const uploadHeroImage = async (file: File | null) => {
    if (!file) return;

    setUploadingImage(true);
    setImageError("");

    try {
      await api.uploadHeroImage(site.id, file, heroCaption);
    } catch (error) {
      console.error("[Dive Sites] Hero image upload failed:", error);
      setImageError(error instanceof Error ? error.message : "Image could not be uploaded.");
    } finally {
      setUploadingImage(false);
    }
  };

  const doDelete = () => { site.isCustom ? api.deleteCustom(site.id) : api.removeFromList(site.id); onClose(); };

  return (
    <div className="ds-modal">
      <div className="ds-mh">
        <div><h3>{site.isCustom ? name || site.name : name || site.name}</h3><div className="orig">{orig}</div></div>
        <button className="x" onClick={onClose}><IconX /></button>
      </div>
      <div className="ds-mb">
        <div className="ds-flabel first">Your name for this site</div>
        <input className="ds-inp" value={name} onChange={e => setName(e.target.value)} placeholder={site.isCustom ? "Site name" : "Leave blank to use the catalog name"} />
        <div className="ds-hint">{nameHint}</div>

        <div className="ds-flabel">Your description</div>
        <textarea className="ds-inp" value={desc} onChange={e => setDesc(e.target.value)} placeholder={site.isCustom ? "Description" : "Leave blank to use the catalog description"} />

        <div className="ds-flabel">Experience image</div>
        <div className="ds-media-block">
          <div className="ds-media-preview">
            {site.heroImageUrl ? (
              <img src={site.heroImageUrl} alt="" />
            ) : (
              <div className="ds-media-empty">No dive site image yet</div>
            )}
          </div>
          <div className="ds-media-actions">
            <input className="ds-inp" value={heroCaption} onChange={e => setHeroCaption(e.target.value)} placeholder="Image caption for concierge and briefing views" />
            <label className="ds-upload-btn">
              {uploadingImage ? "Uploading..." : site.heroImageUrl ? "Replace image" : "Upload image"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={uploadingImage}
                onChange={event => uploadHeroImage(event.target.files?.[0] || null)}
              />
            </label>
            <div className="ds-hint">Caption is stored with the uploaded image.</div>
            {imageError ? <div className="ds-field-error">{imageError}</div> : null}
          </div>
        </div>

        <div className="ds-flabel">Shows in your dive dropdown as</div>
        <div className="ds-preview">{preview}</div>

        <div className="ds-flabel">Status</div>
        <div>
          <span className={`ds-sw ${active ? "on" : ""}`} onClick={() => setActive(a => !a)}>
            <span className="track" />{active ? "Active — you dive this site" : "Inactive — resting"}
          </span>
        </div>

        <div className="ds-flabel">{site.isCustom ? "Your site details" : "Catalog details"}</div>
        <div className="ds-facts">
          {[["Type", site.type], ["Entry", site.entry], ["Skill level", site.skill], ["Depth", `${site.depthMin}–${site.depthMax} m`]].map(([k, v]) => (
            <div className="ds-frow" key={k}><span>{k}</span><b>{v}</b></div>
          ))}
        </div>
        <div className="ds-hint">{site.isCustom ? "You created this site, so these are yours." : `From the shared ${api.destinationName} catalog. Read-only in Viking; per-operator depth and skill overrides are a Pathfinder item.`}</div>
      </div>

      {confirmDel ? (
        <div className="ds-mf">
          <span style={{ fontSize: "12.5px", color: "var(--ink-2)" }}>{site.isCustom ? "Delete this custom site? This cannot be undone." : "Remove this site from your list?"}</span>
          <div className="ds-spacer" />
          <button className="ds-btn" onClick={() => setConfirmDel(false)}>Cancel</button>
          <button className="ds-btn danger" onClick={doDelete}>{site.isCustom ? "Delete site" : "Remove"}</button>
        </div>
      ) : (
        <div className="ds-mf">
          <button className="ds-btn danger" onClick={() => setConfirmDel(true)}>{site.isCustom ? "Delete site" : "Remove from my list"}</button>
          <div className="ds-spacer" />
          <button className="ds-btn" onClick={onClose}>Cancel</button>
          <button className="ds-btn primary" onClick={save}>Save changes</button>
        </div>
      )}
    </div>
  );
}

/* ---------- create modal ---------- */
function CreateModal({ api, onClose }: { api: ReturnType<typeof useDiveSites>; onClose: () => void; }) {
  const [f, setF] = useState<NewCustomSite>({ name: "", type: "Reef", entry: "Shore", skill: "Beginner", depthMin: 0, depthMax: 12, description: "" });
  const [err, setErr] = useState(false);
  const up = (k: keyof NewCustomSite, v: string | number) => setF(prev => ({ ...prev, [k]: v }));
  const create = () => {
    if (!f.name.trim()) { setErr(true); return; }
    api.createCustom({ ...f, name: f.name.trim(), description: f.description.trim() || "Your custom site." });
    onClose();
  };
  return (
    <div className="ds-modal wide">
      <div className="ds-mh"><div><h3>Create a custom site</h3><div className="orig">Private to your center — goes straight into your list.</div></div>
        <button className="x" onClick={onClose}><IconX /></button></div>
      <div className="ds-mb">
        <div className="ds-flabel first">Site name</div>
        <input className="ds-inp" value={f.name} onChange={e => { up("name", e.target.value); setErr(false); }} placeholder="e.g. Nesima House Reef" style={err ? { borderColor: "var(--warm)" } : undefined} />
        <div className="ds-row2" style={{ marginTop: 4 }}>
          <div><div className="ds-flabel">Type</div>
            <select className="ds-inp" value={f.type} onChange={e => up("type", e.target.value)}>{["Reef", "Wall", "Wreck", "Cavern", "Drift"].map(o => <option key={o}>{o}</option>)}</select></div>
          <div><div className="ds-flabel">Entry</div>
            <select className="ds-inp" value={f.entry} onChange={e => up("entry", e.target.value)}>{["Shore", "Boat"].map(o => <option key={o}>{o}</option>)}</select></div>
        </div>
        <div className="ds-row2">
          <div><div className="ds-flabel">Skill level</div>
            <select className="ds-inp" value={f.skill} onChange={e => up("skill", e.target.value)}>{["Beginner", "Intermediate", "Advanced"].map(o => <option key={o}>{o}</option>)}</select></div>
          <div><div className="ds-flabel">Depth min–max (m)</div>
            <div className="ds-row2">
              <input className="ds-inp" type="number" value={f.depthMin} onChange={e => up("depthMin", Number(e.target.value) || 0)} />
              <input className="ds-inp" type="number" value={f.depthMax} onChange={e => up("depthMax", Number(e.target.value) || 0)} />
            </div></div>
        </div>
        <div className="ds-flabel">Description</div>
        <textarea className="ds-inp" value={f.description} onChange={e => up("description", e.target.value)} placeholder="What makes this site yours?" />
      </div>
      <div className="ds-mf"><div className="ds-spacer" /><button className="ds-btn" onClick={onClose}>Cancel</button><button className="ds-btn primary" onClick={create}>Create and add to my list</button></div>
    </div>
  );
}


/* ---------- meet location modal ---------- */
function MeetLocationModal({ meetLocation, api, onClose }: { meetLocation?: MeetLocation; api: ReturnType<typeof useDiveSites>; onClose: () => void; }) {
  const [name, setName] = useState(meetLocation?.name ?? "");
  const [active, setActive] = useState(meetLocation?.active ?? true);
  const [err, setErr] = useState(false);

  const save = () => {
    const nm = name.trim();
    if (!nm) { setErr(true); return; }
    const payload: NewMeetLocation = {
      name: nm,
      active,
    };

    if (meetLocation) api.updateMeetLocation(meetLocation.id, payload);
    else api.createMeetLocation(payload);
    onClose();
  };

  return (
    <div className="ds-modal wide">
      <div className="ds-mh">
        <div>
          <h3>{meetLocation ? `Edit ${meetLocation.name}` : "Create meet location"}</h3>
          <div className="orig">Customer-facing arrival point for schedules, Walk-Ins, and the booking path.</div>
        </div>
        <button className="x" onClick={onClose}><IconX /></button>
      </div>
      <div className="ds-mb">
        <div className="ds-flabel first">Location name</div>
        <input className="ds-inp" value={name} onChange={e => { setName(e.target.value); setErr(false); }} placeholder="e.g. South Beach Dock" style={err ? { borderColor: "var(--warm)" } : undefined} />

        <div className="ds-flabel">Status</div>
        <span className={`ds-sw ${active ? "on" : ""}`} onClick={() => setActive(value => !value)}>
          <span className="track" />{active ? "Active — selectable for the day" : "Inactive — resting"}
        </span>
      </div>
      <div className="ds-mf">
        <div className="ds-spacer" />
        <button className="ds-btn" onClick={onClose}>Cancel</button>
        <button className="ds-btn primary" onClick={save}>{meetLocation ? "Save meet location" : "Create meet location"}</button>
      </div>
    </div>
  );
}

/* ---------- catalog modal ---------- */
function CatalogModal({ sites, api, onClose }: { sites: DiveSite[]; api: ReturnType<typeof useDiveSites>; onClose: () => void; }) {
  const rows = [...sites.filter(s => !s.isCustom)].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="ds-modal wide">
      <div className="ds-mh"><h3>Add sites from the {api.destinationName} catalog</h3><button className="x" onClick={onClose}><IconX /></button></div>
      <div className="ds-mb">
        {rows.map(s => (
          <div className="ds-catrow" key={s.id}>
            <div style={{ flex: 1 }}>
              <div className="cn">{s.name}</div>
              <div className="cm">
                <span className="ds-chip water">{s.type}</span><span className="ds-chip">{s.entry}</span>
                <span className="ds-chip">{s.skill}</span><span className="ds-chip">{s.depthMin}–{s.depthMax}m</span>
              </div>
            </div>
            <button className={`ds-addbtn ${s.inList ? "added" : ""}`} onClick={() => (s.inList ? api.removeFromList(s.id) : api.addToList(s.id))}>
              {s.inList ? <><IconCheck /> In your list</> : "Add"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function DiveCategoriesSection({
  categories,
  search,
  setSearch,
  onToggleActive,
}: {
  categories: DiveCategory[];
  search: string;
  setSearch: (value: string) => void;
  onToggleActive: (id: number, active: boolean) => void;
}) {
  return (
    <>
      <div className="ds-meet-head">
        <div>
          <div className="ds-secrow"><span className="ds-seclabel">Dive categories</span><span className="n">{categories.length}</span></div>
          <h2>How sessions are labeled</h2>
          <p>Use dive categories to label sessions on Schedule, Ops Board, widget, and customer-facing surfaces.</p>
        </div>
        <div className="ds-meet-actions">
          <input className="ds-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dive categories" />
        </div>
      </div>

      <div className="ds-tablewrap ds-meet-table">
        <table>
          <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
          </thead>
          <tbody>
          {categories.map(category => (
            <tr key={category.id}>
              <td>
                <span className="ds-sname">{category.label}</span>
              </td>
              <td><span className={`ds-pill ${category.active ? "active" : "quiet"}`}><span className="d" />{category.active ? "Active" : "Inactive"}</span></td>
              <td className="act">
                <button className={`ds-btn ${category.active ? "" : "reef"}`} onClick={() => onToggleActive(category.id, !category.active)}>
                  {category.active ? "Deactivate" : "Reactivate"}
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

